import React, { useCallback, useEffect } from "react";

import { useDispatch } from "react-redux";

import CircularProgress from "@busbud/design-system-components/dist/CircularProgress";
import Divider from "@busbud/design-system-components/dist/Divider";
import Stack from "@busbud/design-system-components/dist/Stack";

import { ReviewItem } from "@app/components/landing-pages/operators/components/review-item";
import { RatingsBreakdown } from "@app/components/ratings-breakdown/ratings-breakdown";
import { useAppContext } from "@app/helpers/hooks";
import { OPERATOR_PROFILE_REVIEWS_STATUS } from "@app/modules/results/constants/profile-reviews";
import { useResultsSelector } from "@app/modules/results/hooks/use-results-selector";
import {
  getOperatorProfileReviews,
  getOperatorProfileRatings
} from "@app/modules/results/selectors/state-accessors";
import { fetchProfileReviews } from "@app/modules/results/state/profile-reviews";

import type { ConnectorProps } from "./operator-ratings.connect";

import { OperatorRatingCategories } from "./operator-rating-categories";
import { useStyles } from "./operator-ratings.styles";

export const OperatorRatings: React.FC<ConnectorProps> = ({
  profile_id,
  display_operator,
  display_operator_rating,
  rating_categories
}) => {
  const { classes } = useStyles();
  const { api } = useAppContext();

  const all_operator_profile_ratings = useResultsSelector(
    getOperatorProfileRatings
  );
  const all_operator_profile_reviews = useResultsSelector(
    getOperatorProfileReviews
  );

  const dispatch = useDispatch<any>();
  const loadProfileReviews = useCallback(
    (profile_id: number) => dispatch(fetchProfileReviews({ api, profile_id })),
    [dispatch, api]
  );

  useEffect(() => {
    if (profile_id) {
      const reviews = all_operator_profile_reviews[profile_id];
      if (
        !reviews ||
        reviews.status === OPERATOR_PROFILE_REVIEWS_STATUS.FAILED
      ) {
        loadProfileReviews(profile_id);
      }
    }
  }, [profile_id, all_operator_profile_reviews, loadProfileReviews]);

  if (!profile_id || !display_operator || !display_operator_rating) {
    return null;
  }

  const profile_ratings = all_operator_profile_ratings[profile_id];
  if (!profile_ratings) {
    return null;
  }

  const profile_reviews = all_operator_profile_reviews[profile_id];
  if (
    !profile_reviews ||
    profile_reviews.status === OPERATOR_PROFILE_REVIEWS_STATUS.FAILED
  ) {
    return null;
  }

  const renderLoader = () => (
    <div className={classes.loader}>
      <CircularProgress />
    </div>
  );

  return (
    <Stack direction="column" spacing={2}>
      <OperatorRatingCategories categories={rating_categories} />

      {profile_reviews.status !== OPERATOR_PROFILE_REVIEWS_STATUS.SUCCEEDED ? (
        renderLoader()
      ) : (
        <>
          <div className="flex flex-col gap-300">
            {profile_reviews.reviews.map(review => (
              <ReviewItem key={review.id} review={review} />
            ))}
          </div>
          <Divider />
          {!!profile_ratings.ratings_breakdown && (
            <RatingsBreakdown
              ratings_breakdown={profile_ratings.ratings_breakdown}
              global_rating={display_operator_rating.global_rating}
              count_total={display_operator_rating.count_total}
            />
          )}
        </>
      )}
    </Stack>
  );
};
